import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { authErrorInterceptor } from './core/interceptors/auth-error.interceptor';
import { licenseInterceptor } from './core/interceptors/license.interceptor';
import { toastInterceptor } from './core/interceptors/toast.interceptor';

import { provideEchartsCore } from 'ngx-echarts';

// ngx-loading-bar -> barra de progresso superior na aplicacao
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { authInterceptor, provideAuth } from 'angular-auth-oidc-client';
import { authConfig } from './core/auth/auth.config';

import * as echarts from 'echarts/core';

import { provideIcons } from '@ng-icons/core';
import { APP_ICONS } from './shared/icons/app-icons';
import { provideCalendarI18n } from './shared/utils/calendar-i18n';
import { provideFormlyCore } from '@ngx-formly/core';
import { CredentialPickerFieldComponent } from './shared/forms/field-types/credential-picker.type';
import { ChipsListFieldComponent } from './shared/forms/field-types/chips-list.type';
import { RepeatSectionFieldComponent } from './shared/forms/field-types/repeat-section.type';
import { FieldsetWrapperComponent } from './shared/forms/field-types/fieldset-wrapper.type';
import { FormFieldWrapperComponent } from './shared/forms/field-types/form-field-wrapper.type';
import { InputFieldComponent } from './shared/forms/field-types/input.type';
import { SelectFieldComponent } from './shared/forms/field-types/select.type';
import { CheckboxFieldComponent } from './shared/forms/field-types/checkbox.type';

export const appConfig: ApplicationConfig = {
  providers: [
    // Ícones lucide globais (migração primeicons → ng-icons)
    provideIcons(APP_ICONS),

    // Calendários spartan: adapter de data, i18n inicial e sincronização com o
    // idioma corrente.
    provideCalendarI18n(),
    // Sem zona: não há mais tick automático. Todo componente da app é OnPush e a
    // notificação vem de signal, binding, listener de template ou markForCheck —
    // `eventCoalescing` deixa de fazer sentido porque o zoneless já agrupa.
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideEchartsCore({ echarts }),

    provideAuth(authConfig),

    provideHttpClient(
      withXhr(),
      // permite que o interceptor do @ngx-loading-bar/http-client funcione
      withInterceptorsFromDi(),
      withInterceptors([
        toastInterceptor,
        authInterceptor(),
        authErrorInterceptor,
        licenseInterceptor,
        // (opcional) um interceptor-fn de debug para inspecionar o header:
        // motivo: algumas chamadas para LLM estavam indo sem beearer token
        // (req, next) => {
        //   if (req.url.startsWith('https://evolve-api.azurewebsites.net')) {
        //     console.log('[REQ]', req.url, 'Auth?', !!req.headers.get('Authorization'));
        //     console.log(req.headers)
        //   }
        //   return next(req);
        // },
      ]),
    ),

    {
      provide: LOCALE_ID,
      useFactory: (translate: TranslateService) => {
        return translate.getCurrentLang();
      },
      deps: [TranslateService],
    },

    // Caso for aplicar o cache nas chamadas sme o CubeJS
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: CacheInterceptor,
    //   multi: true,
    // },

    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'pt-BR',
      lang: 'pt-BR',
    }),

    // Importa módulos NgModule do ngx-loading-bar
    importProvidersFrom(
      LoadingBarModule, // componente + serviço
      LoadingBarRouterModule, // mostra barra em navegações de rota
      LoadingBarHttpClientModule, // mostra barra em requisições HttpClient
    ),

    // Formly + spartan: field types próprios (input, select, checkbox) com o
    // wrapper form-field (label em cima + validação) e os types custom.
    //
    // `provideFormlyCore` e não `provideFormlyConfig`: o primeiro é o
    // equivalente standalone de `FormlyModule.forRoot()` e instala as extensions
    // do core; o segundo só acrescenta config (types, wrappers) a um core que
    // precisa já existir. Com apenas o segundo, `FormlyFormBuilder.build()`
    // aborta em `!this.config.extensions.core` e todo form Formly quebra ao
    // abrir — era o caso dos modais de conector e de credencial.
    provideFormlyCore({
      types: [
        {
          name: 'input',
          component: InputFieldComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'select',
          component: SelectFieldComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'checkbox',
          component: CheckboxFieldComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'credential-picker',
          component: CredentialPickerFieldComponent,
        },
        {
          name: 'chips-list',
          component: ChipsListFieldComponent,
        },
        {
          name: 'repeat',
          component: RepeatSectionFieldComponent,
        },
      ],
      wrappers: [
        {
          name: 'form-field',
          component: FormFieldWrapperComponent,
        },
        {
          name: 'fieldset',
          component: FieldsetWrapperComponent,
        },
      ],
    }),
  ],
};
